/**
 * AddStopModal — Two-tab dialog for adding stops to a crew route.
 *
 * Tab 1: "Aspire Properties" — debounced search against RoutingProperty browse endpoint.
 * Tab 2: "Manual Address"   — free-text address geocoded via the user-routes geocode endpoint.
 *
 * Props:
 *   open       {boolean}  — controls dialog visibility
 *   onClose    {function} — called when dialog should close
 *   onAddStop  {function} — called with the new stop object
 *   baseUrl    {string}   — axios base URL (unused directly; axios picks it up from defaults)
 */

import React, { useState, useRef } from 'react'
import axios from 'axios'
import { makeStyles } from '@material-ui/core/styles'
import Dialog from '@material-ui/core/Dialog'
import DialogTitle from '@material-ui/core/DialogTitle'
import DialogContent from '@material-ui/core/DialogContent'
import DialogActions from '@material-ui/core/DialogActions'
import Button from '@material-ui/core/Button'
import TextField from '@material-ui/core/TextField'
import Tabs from '@material-ui/core/Tabs'
import Tab from '@material-ui/core/Tab'
import List from '@material-ui/core/List'
import ListItem from '@material-ui/core/ListItem'
import ListItemText from '@material-ui/core/ListItemText'
import Typography from '@material-ui/core/Typography'
import CircularProgress from '@material-ui/core/CircularProgress'
import Box from '@material-ui/core/Box'
import Divider from '@material-ui/core/Divider'

const useStyles = makeStyles((theme) => ({
  dialogContent: {
    padding: theme.spacing(0),
    display: 'flex',
    flexDirection: 'column',
    minHeight: 340,
  },
  tabPanel: {
    padding: theme.spacing(2),
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
  },
  searchField: {
    marginBottom: theme.spacing(1.5),
  },
  resultsList: {
    flex: 1,
    overflowY: 'auto',
    maxHeight: 260,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
  },
  resultItem: {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  loadingBox: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(3),
  },
  emptyHint: {
    padding: theme.spacing(2),
    color: theme.palette.text.disabled,
    textAlign: 'center',
  },
  manualField: {
    marginBottom: theme.spacing(2),
  },
  errorText: {
    color: theme.palette.error.main,
    marginTop: theme.spacing(1),
    fontSize: '0.85rem',
  },
}))

function TabPanel({ children, value, index }) {
  if (value !== index) return null
  return <div>{children}</div>
}

function AddStopModal({ open, onClose, onAddStop, baseUrl }) {
  const classes = useStyles()

  // ── Tab state ──────────────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState(0)

  // ── Aspire Properties tab state ────────────────────────────────────────────
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState([])
  const [searching, setSearching] = useState(false)
  const [searchError, setSearchError] = useState('')
  const timerRef = useRef(null)

  // ── Manual Address tab state ───────────────────────────────────────────────
  const [manualAddress, setManualAddress] = useState('')
  const [geocoding, setGeocoding] = useState(false)
  const [geocodeError, setGeocodeError] = useState('')

  // ── Helpers ────────────────────────────────────────────────────────────────

  const resetState = () => {
    setActiveTab(0)
    setSearchQuery('')
    setSearchResults([])
    setSearching(false)
    setSearchError('')
    setManualAddress('')
    setGeocoding(false)
    setGeocodeError('')
    clearTimeout(timerRef.current)
  }

  const handleClose = () => {
    resetState()
    onClose()
  }

  // ── Tab 1: Property search ─────────────────────────────────────────────────

  const searchProperties = async (query) => {
    const trimmed = (query || '').trim()
    if (!trimmed) {
      setSearchResults([])
      return
    }

    setSearching(true)
    setSearchError('')

    try {
      const response = await axios.get(
        `${baseUrl}/api/aspire/routing/browse/properties`,
        { params: { search: trimmed, limit: 20 } }
      )
      // API returns { docs, total, page, limit }
      const docs = (response.data && response.data.docs) || []
      setSearchResults(docs)
    } catch (err) {
      console.error('[AddStopModal] property search error', err)
      setSearchError('Search failed. Please try again.')
      setSearchResults([])
    } finally {
      setSearching(false)
    }
  }

  const handleSearchChange = (e) => {
    const value = e.target.value
    setSearchQuery(value)
    clearTimeout(timerRef.current)
    timerRef.current = setTimeout(() => searchProperties(value), 300)
  }

  const handleSelectProperty = (property) => {
    // AspireProperty uses PascalCase fields from Aspire API
    const stop = {
      stopId: `aspire-${property.PropertyID || property._id}-${Date.now()}`,
      sourceType: 'jobsite',
      siteId: property.jobsiteId || null,
      name: property.PropertyName || property.name || '',
      address: property.PropertyAddressLine1 || property.address1 || '',
      city: property.PropertyAddressCity || property.city || '',
      state: property.PropertyAddressStateProvinceCode || property.state || '',
      latitude: property.GEOLocationLatitude || property.latitude,
      longitude: property.GEOLocationLongitude || property.longitude,
      serviceMinutes: 15,
      order: null,
      notes: '',
      files: [],
    }
    onAddStop(stop)
    handleClose()
  }

  // ── Tab 2: Manual address ──────────────────────────────────────────────────

  const handleAddManualAddress = async () => {
    const trimmed = manualAddress.trim()
    if (!trimmed) {
      setGeocodeError('Please enter an address.')
      return
    }

    setGeocoding(true)
    setGeocodeError('')

    try {
      const response = await axios.post('/api/mobileapp/user-routes/geocode-stop', {
        address: trimmed,
      })
      const data = response.data || {}

      const stop = {
        stopId: `manual-${Date.now()}`,
        sourceType: 'manual',
        siteId: null,
        name: data.name || trimmed,
        address: data.address || trimmed,
        city: data.city || '',
        state: data.state || '',
        latitude: data.latitude,
        longitude: data.longitude,
        serviceMinutes: 15,
        order: null,
        notes: '',
        files: [],
      }
      onAddStop(stop)
      handleClose()
    } catch (err) {
      console.error('[AddStopModal] geocode error', err)
      const msg =
        (err.response && err.response.data && err.response.data.message) ||
        'Geocoding failed. Please try again.'
      setGeocodeError(msg)
    } finally {
      setGeocoding(false)
    }
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>Add Stop</DialogTitle>

      <Divider />

      <Tabs
        value={activeTab}
        onChange={(_, val) => setActiveTab(val)}
        indicatorColor="primary"
        textColor="primary"
        variant="fullWidth"
      >
        <Tab label="Aspire Properties" />
        <Tab label="Manual Address" />
      </Tabs>

      <Divider />

      <DialogContent className={classes.dialogContent}>
        {/* ── Tab 1: Aspire Properties ── */}
        <TabPanel value={activeTab} index={0}>
          <Box className={classes.tabPanel}>
            <TextField
              className={classes.searchField}
              label="Search properties"
              variant="outlined"
              size="small"
              fullWidth
              value={searchQuery}
              onChange={handleSearchChange}
              autoFocus
              placeholder="Type a property name or address…"
            />

            {searching ? (
              <Box className={classes.loadingBox}>
                <CircularProgress size={28} />
              </Box>
            ) : searchError ? (
              <Typography className={classes.errorText}>{searchError}</Typography>
            ) : searchResults.length === 0 ? (
              <Typography className={classes.emptyHint}>
                {searchQuery.trim()
                  ? 'No properties found.'
                  : 'Start typing to search Aspire properties.'}
              </Typography>
            ) : (
              <List disablePadding className={classes.resultsList}>
                {searchResults.map((property, idx) => (
                  <ListItem
                    key={property._id || property.propertyId || idx}
                    className={classes.resultItem}
                    onClick={() => handleSelectProperty(property)}
                    divider={idx < searchResults.length - 1}
                  >
                    <ListItemText
                      primary={property.PropertyName || property.name || 'Unknown'}
                      secondary={property.PropertyAddressLine1 || property.address1 || '—'}
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </Box>
        </TabPanel>

        {/* ── Tab 2: Manual Address ── */}
        <TabPanel value={activeTab} index={1}>
          <Box className={classes.tabPanel}>
            <TextField
              className={classes.manualField}
              label="Address"
              variant="outlined"
              size="small"
              fullWidth
              value={manualAddress}
              onChange={(e) => {
                setManualAddress(e.target.value)
                setGeocodeError('')
              }}
              placeholder="123 Main St, Phoenix, AZ 85001"
              disabled={geocoding}
            />

            {geocodeError ? (
              <Typography className={classes.errorText}>{geocodeError}</Typography>
            ) : null}
          </Box>
        </TabPanel>
      </DialogContent>

      <Divider />

      <DialogActions>
        <Button onClick={handleClose} disabled={geocoding}>
          Cancel
        </Button>

        {activeTab === 1 ? (
          <Button
            variant="contained"
            color="primary"
            onClick={handleAddManualAddress}
            disabled={geocoding || !manualAddress.trim()}
          >
            {geocoding ? <CircularProgress size={20} /> : 'Add Address'}
          </Button>
        ) : null}
      </DialogActions>
    </Dialog>
  )
}

export default AddStopModal
